import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Stack,
  Accordion,
} from "@chakra-ui/react";
import { Card } from "@chakra-ui/react/card";
import {
  MdLink as LinkIcon,
  MdCameraAlt as CameraIcon,
  MdMic as MicIcon,
  MdEdit as EditIcon,
  MdStar as StarIcon,
  MdFolder as FolderIcon,
  MdChat as ChatIcon,
  MdLightMode as CookModeIcon,
  MdSearch as SearchIcon,
  MdShare as ShareIcon,
} from "react-icons/md";
import Header from "@/components/Header";
import BottomNavBar from "@/components/BottomNavBar";

const importMethods = [
  {
    Icon: LinkIcon,
    title: "Import from URL",
    description: "Copy and paste recipe URLs from popular cooking websites like BBC Good Food, AllRecipes, Food Network, and more. Our AI will automatically extract the recipe details, ingredients, instructions, and images.",
  },
  {
    Icon: CameraIcon,
    title: "Upload Images",
    description: "Upload photos of recipe cards, cookbook pages, or handwritten recipes. Our OCR technology extracts text from images, and AI structures it into a proper recipe format.",
  },
  {
    Icon: MicIcon,
    title: "Text Input",
    description: "Type or paste your recipe text and our AI will automatically format it into a structured recipe with ingredients, instructions, and cooking details.",
  },
  {
    Icon: EditIcon,
    title: "Manual Entry",
    description: "Create recipes from scratch using our intuitive form. Add ingredients, cooking steps, timing, and all the details manually with full control over formatting.",
  },
];

const features = [
  {
    Icon: StarIcon,
    title: "Rate Your Recipes",
    description: "Rate recipes with our simple system — loved (5 stars), liked (4 stars), or disliked (2 stars). Filter your collection by ratings to find your favourites quickly.",
  },
  {
    Icon: FolderIcon,
    title: "Organise with Collections",
    description: "Group recipes into collections like 'Sunday Dinners', 'Quick Weeknight Meals', or 'Holiday Baking'. Each collection includes a consolidated shopping list.",
  },
  {
    Icon: ChatIcon,
    title: "AI Chef Assistant",
    description: "Get cooking advice, ingredient substitutions, and recipe suggestions from our AI chef. Available on recipe pages and throughout the app for personalised help.",
  },
  {
    Icon: CookModeIcon,
    title: "Cook mode",
    description: "Turn on Cook mode on any recipe to keep your screen awake while you cook.",
  },
  {
    Icon: SearchIcon,
    title: "Smart Search & Tags",
    description: "Find recipes by ingredients, cuisine type, dietary needs, or cooking time. Our AI automatically tags recipes with relevant categories for easy discovery.",
  },
  {
    Icon: ShareIcon,
    title: "Share Recipes",
    description: "Share your favourite recipes with friends and family. Import recipes directly from social media shares using our deep linking system.",
  },
];

const faqs = [
  {
    value: "1",
    question: "Which recipe websites are supported for URL imports?",
    answer: "Byte Pantry works with most popular cooking websites including BBC Good Food, AllRecipes, Food Network, Bon Appétit, Serious Eats, and many more. Our AI can extract recipes from any website that displays structured recipe information.",
  },
  {
    value: "2",
    question: "How accurate is the image text recognition?",
    answer: "Our OCR technology works best with clear, well-lit images of printed text. For handwritten recipes, accuracy depends on legibility. After processing, you can always edit the extracted text to make corrections.",
  },
  {
    value: "3",
    question: "Can I edit recipes after importing them?",
    answer: "Yes! Every recipe can be edited after import. Click the three-dot menu on any recipe and select \"Edit\" to modify ingredients, instructions, cooking times, or add personal notes and modifications.",
  },
  {
    value: "4",
    question: "How do collections and shopping lists work?",
    answer: "Collections let you group recipes by theme, occasion, or meal planning. Each collection automatically generates a consolidated shopping list that combines ingredients from all recipes, with intelligent quantity consolidation and the ability to mark items as \"have it\" or \"need to buy\".",
  },
  {
    value: "5",
    question: "What can the AI Chef assistant help with?",
    answer: "The AI Chef can help with ingredient substitutions, cooking techniques, recipe modifications, serving size adjustments, and general cooking advice. It's context-aware and can provide specific help related to the recipe you're viewing.",
  },
  {
    value: "6",
    question: "Is my recipe data private and secure?",
    answer: "Yes, your recipes are completely private to your account. We use secure authentication and your recipe data is only visible to you. Recipe images are stored securely in object storage with proper access controls.",
  },
  {
    value: "7",
    question: "Can I use Byte Pantry on my mobile device?",
    answer: "Byte Pantry is fully responsive and works great on mobile browsers. We also have native mobile apps for iOS and Android with additional features like native camera integration and offline support.",
  },
  {
    value: "8",
    question: "How do I share recipes with others?",
    answer: "You can share recipes directly from the recipe page using the share button. This creates a shareable link that others can view even without an account. You can also import recipes directly from social media by sharing recipe URLs to the Byte Pantry app.",
  },
  {
    value: "9",
    question: "Can I keep my screen on while cooking?",
    answer: "Yes. Turn on Cook mode on any recipe and your screen won't dim or lock. It switches off when you leave the recipe.",
  },
];

export default function Help() {
  return (
    <Box minH="100vh" bg="bg.canvas" pb={{ base: "80px", lg: 0 }}>
      <Header title="Help" />
      <Container maxW="4xl" py={8}>
        <VStack gap={8} align="stretch">

          {/* Hero */}
          <Box textAlign="center">
            <Heading
              fontFamily="heading"
              fontWeight="800"
              fontSize="2xl"
              color="text.primary"
              mb={2}
            >
              Byte Pantry Help Centre
            </Heading>
            <Text fontFamily="body" fontWeight="300" fontSize="md" color="text.muted">
              Your AI-powered recipe collection made simple
            </Text>
          </Box>

          {/* Getting Started */}
          <Card.Root bg="bg.surface" border="1px solid" borderColor="border.muted" shadow="none">
            <Card.Body>
              <Heading fontFamily="heading" fontWeight="800" fontSize="lg" color="text.primary" mb={4}>
                Getting Started
              </Heading>
              <Text fontFamily="body" fontWeight="300" color="text.primary" mb={4}>
                Byte Pantry makes it easy to build your personal recipe collection using AI technology.
                Here's how to get the most out of the app:
              </Text>
              <VStack gap={2} align="start">
                {[
                  ["Import recipes", "using any of our four import methods"],
                  ["Rate and organise", "your recipes for easy discovery"],
                  ["Create collections", "for meal planning and shopping"],
                  ["Use our AI chef", "for cooking advice and substitutions"],
                ].map(([bold, rest], i) => (
                  <Text key={i} fontFamily="body" fontWeight="300" color="text.primary">
                    •{" "}
                    <Text as="span" fontWeight="600">{bold}</Text>
                    {" "}{rest}
                  </Text>
                ))}
              </VStack>
            </Card.Body>
          </Card.Root>

          {/* Import methods */}
          <Box>
            <Heading fontFamily="heading" fontWeight="800" fontSize="lg" color="text.primary" mb={6}>
              How to Import Recipes
            </Heading>
            <Stack gap={3}>
              {importMethods.map((method, i) => (
                <Card.Root key={i} bg="bg.surface" border="1px solid" borderColor="border.muted" shadow="none">
                  <Card.Body>
                    <HStack gap={4} align="start">
                      <Box color="accent.secondary" flexShrink={0} mt="2px" fontSize="22px">
                        <method.Icon />
                      </Box>
                      <VStack align="start" gap={1}>
                        <Heading fontFamily="heading" fontWeight="800" fontSize="sm" color="text.primary">
                          {method.title}
                        </Heading>
                        <Text fontFamily="body" fontWeight="300" fontSize="sm" color="text.muted">
                          {method.description}
                        </Text>
                      </VStack>
                    </HStack>
                  </Card.Body>
                </Card.Root>
              ))}
            </Stack>
          </Box>

          {/* Key features */}
          <Box>
            <Heading fontFamily="heading" fontWeight="800" fontSize="lg" color="text.primary" mb={6}>
              Key Features
            </Heading>
            <Stack gap={3}>
              {features.map((feature, i) => (
                <Card.Root key={i} bg="bg.surface" border="1px solid" borderColor="border.muted" shadow="none">
                  <Card.Body>
                    <HStack gap={4} align="start">
                      <Box color="accent.secondary" flexShrink={0} mt="2px" fontSize="22px">
                        <feature.Icon />
                      </Box>
                      <VStack align="start" gap={1}>
                        <Heading fontFamily="heading" fontWeight="800" fontSize="sm" color="text.primary">
                          {feature.title}
                        </Heading>
                        <Text fontFamily="body" fontWeight="300" fontSize="sm" color="text.muted">
                          {feature.description}
                        </Text>
                      </VStack>
                    </HStack>
                  </Card.Body>
                </Card.Root>
              ))}
            </Stack>
          </Box>

          {/* FAQ */}
          <Box>
            <Heading fontFamily="heading" fontWeight="800" fontSize="lg" color="text.primary" mb={6}>
              Frequently Asked Questions
            </Heading>
            <Box border="1px solid" borderColor="border.muted" borderRadius="lg" overflow="hidden" bg="bg.surface">
              <Accordion.Root multiple>
                {faqs.map((faq) => (
                  <Accordion.Item key={faq.value} value={faq.value} borderColor="border.muted">
                    <Accordion.ItemTrigger px={4} py={4}>
                      <Box flex="1" textAlign="left">
                        <Text fontFamily="body" fontWeight="500" fontSize="sm" color="text.primary">
                          {faq.question}
                        </Text>
                      </Box>
                      <Accordion.ItemIndicator color="text.muted" />
                    </Accordion.ItemTrigger>
                    <Accordion.ItemContent>
                      <Accordion.ItemBody px={4} pb={4}>
                        <Text fontFamily="body" fontWeight="300" fontSize="sm" color="text.muted">
                          {faq.answer}
                        </Text>
                      </Accordion.ItemBody>
                    </Accordion.ItemContent>
                  </Accordion.Item>
                ))}
              </Accordion.Root>
            </Box>
          </Box>

          {/* Tips */}
          <Card.Root bg="bg.surface" border="1px solid" borderColor="border.muted" shadow="none">
            <Card.Body>
              <Heading fontFamily="heading" fontWeight="800" fontSize="lg" color="text.primary" mb={4}>
                Tips for Best Results
              </Heading>
              <VStack gap={2} align="start">
                {[
                  ["URL imports:", "Copy the full recipe URL, not just the website homepage"],
                  ["Image uploads:", "Use clear, well-lit photos with readable text"],
                  ["Text input:", "Include ingredients with measurements and step-by-step instructions"],
                  ["Collections:", "Group similar recipes together for better shopping list consolidation"],
                  ["Tags:", "Use the search function to find recipes by ingredients, cuisine, or dietary needs"],
                  ["Ratings:", "Rate recipes to easily find your favourites later"],
                ].map(([bold, rest], i) => (
                  <Text key={i} fontFamily="body" fontWeight="300" fontSize="sm" color="text.primary">
                    •{" "}
                    <Text as="span" fontWeight="600">{bold}</Text>
                    {" "}{rest}
                  </Text>
                ))}
              </VStack>
            </Card.Body>
          </Card.Root>

          {/* Footer note */}
          <Box textAlign="center" py={2}>
            <Text fontFamily="body" fontWeight="300" fontSize="sm" color="text.muted">
              Need more help? The AI Chef assistant is available throughout the app to provide
              personalised cooking advice and answer specific questions about your recipes.
            </Text>
          </Box>

        </VStack>
      </Container>
      <BottomNavBar />
    </Box>
  );
}
